/**
 * ChangeTracker - Tracks changes to the recipe graph for save functionality
 *
 * Responsibilities:
 * - Store original graph state for restoration
 * - Track node-level changes (field updates)
 * - Track graph-level changes (add/delete nodes/links)
 * - Provide dirty state for save button visibility
 * - Reset tracking after successful save
 */

import { DisplayGraph } from './DisplayGraph.js';

export interface GraphChanges {
  hasChanges: boolean;
  modifiedNodeIds: Set<string>;
  graphStructureChanged: boolean;
}

export class ChangeTracker {
  private originalGraph: DisplayGraph;
  private modifiedNodeIds: Set<string> = new Set();
  private graphStructureChanged: boolean = false;

  constructor(currentGraph: DisplayGraph) {
    // Store a clone of the current graph state
    this.originalGraph = currentGraph.clone();
  }

  /**
   * Track that a specific node has been modified
   */
  trackNodeChange(nodeId: string): void {
    this.modifiedNodeIds.add(nodeId);
  }

  /**
   * Track that the graph structure has changed (add/delete nodes or links)
   */
  trackGraphChange(): void {
    this.graphStructureChanged = true;
  }

  /**
   * Check if there are any unsaved changes
   */
  hasChanges(): boolean {
    return this.modifiedNodeIds.size > 0 || this.graphStructureChanged;
  }

  /**
   * Get all tracked changes
   */
  getChanges(): GraphChanges {
    return {
      hasChanges: this.hasChanges(),
      modifiedNodeIds: new Set(this.modifiedNodeIds),
      graphStructureChanged: this.graphStructureChanged,
    };
  }

  /**
   * Reset all tracked changes (call after successful save)
   * Updates the original graph to the new current state
   */
  reset(currentGraph: DisplayGraph): void {
    this.modifiedNodeIds.clear();
    this.graphStructureChanged = false;
    this.originalGraph = currentGraph.clone();
  }

  /**
   * Get the original graph state for restoration
   */
  getOriginalGraph(): DisplayGraph {
    return this.originalGraph.clone();
  }
}
